import React, { useState } from 'react';
import './TraceStatistics.css';
import EntityDetailsModal from './EntityDetailsModal';
import FraudScoreCard from './FraudScoreCard';

function TraceStatistics({ traceData, mode }) {
  const [selectedEntity, setSelectedEntity] = useState(null);
  
  if (!traceData) return null;

  const renderSmartStats = (data) => {
    const path = data.path;
    return (
      <div className="stats-content">
        <div className="stat-card primary">
          <div className="stat-icon">🎯</div>
          <div className="stat-info">
            <span className="stat-label">Confidence</span>
            <span className="stat-value">{(path.confidence * 100).toFixed(0)}%</span>
          </div>
          <div className="confidence-bar">
            <div 
              className="confidence-fill" 
              style={{ width: `${path.confidence * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <span className="stat-label">Depth</span>
            <span className="stat-value">{path.depth}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">{path.coinbase_reached ? '⛏️' : '🔍'}</div>
          <div className="stat-info">
            <span className="stat-label">Status</span>
            <span className="stat-value">{path.coinbase_reached ? 'Coinbase' : 'Partial'}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <span className="stat-label">Value</span>
            <span className="stat-value">{path.total_value.toFixed(4)} BTC</span>
          </div>
        </div>

        {path.known_entities && path.known_entities.length > 0 && (
          <div className="entities-section">
            <h4>🏷️ Known Entities</h4>
            <div className="entities-list">
              {path.known_entities.map((entity, idx) => (
                <div 
                  key={idx} 
                  className={`entity-card risk-${entity.risk_level} clickable`}
                  onClick={() => setSelectedEntity(entity)}
                  title="Click for details"
                >
                  <div className="entity-header">
                    <span className="entity-name">{entity.label}</span>
                    <span className={`entity-type type-${entity.entity_type}`}>
                      {entity.entity_type}
                    </span>
                  </div>
                  <div className="entity-details">
                    <span className="entity-address">{entity.address.substring(0, 20)}...</span>
                    <span className={`entity-risk risk-${entity.risk_level}`}>
                      {entity.risk_level}
                    </span>
                  </div>
                  <div className="entity-depth">Depth: {entity.depth}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NEW: Tainted Addresses Section */}
        {path.tainted_addresses && path.tainted_addresses.length > 0 && (
          <div className="entities-section tainted-section">
            <h4>🚨 Tainted Addresses</h4>
            <div className="entities-list">
              {path.tainted_addresses.map((tainted, idx) => (
                <div 
                  key={idx} 
                  className={`entity-card tainted-card ${tainted.verified ? 'verified' : 'auto-detected'}`}
                  title={tainted.verified ? 'Verified tainted address' : 'Auto-detected from WalletExplorer'}
                >
                  <div className="entity-header">
                    <span className="entity-name">
                      {tainted.verified ? '🚨' : '⚠️'} {tainted.description || tainted.category}
                    </span>
                    {!tainted.verified && (
                      <span className="entity-type auto-badge">Auto</span>
                    )}
                  </div>
                  <div className="entity-details">
                    <span className="entity-address" title={tainted.address}>
                      {tainted.address.substring(0, 20)}...
                    </span>
                    <span className={`taint-severity severity-${tainted.severity}`}>
                      Severity: {tainted.severity}/10
                    </span>
                  </div>
                  <div className="entity-depth">
                    Depth: {tainted.depth} • 
                    {tainted.direction === 'input' ? ' ⬅️ Input' : ' ➡️ Output'} • 
                    {tainted.value_btc?.toFixed(4) || '?'} BTC
                  </div>
                  {tainted.tags && tainted.tags.length > 0 && (
                    <div className="tainted-tags-inline">
                      {tainted.tags.slice(0, 3).map((tag, tagIdx) => (
                        <span key={tagIdx} className="tag-badge">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NEW: Fraud Score Card */}
        {path.fraud_score && (
          <FraudScoreCard 
            fraudScore={path.fraud_score} 
            taintedAddresses={path.tainted_addresses || []}
          />
        )}
      </div>
    );
  };

  const renderMultiPathStats = (data) => {
    const paths = data.paths;
    const avgConfidence = paths.reduce((sum, p) => sum + p.confidence, 0) / paths.length;
    const coinbaseReached = paths.filter(p => p.coinbase_reached).length;
    const allEntities = paths.flatMap(p => p.known_entities || []);
    
    return (
      <div className="stats-content">
        <div className="stat-card primary">
          <div className="stat-icon">🔀</div>
          <div className="stat-info">
            <span className="stat-label">Paths Found</span>
            <span className="stat-value">{paths.length}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <span className="stat-label">Avg Confidence</span>
            <span className="stat-value">{(avgConfidence * 100).toFixed(0)}%</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⛏️</div>
          <div className="stat-info">
            <span className="stat-label">Coinbase Reached</span>
            <span className="stat-value">{coinbaseReached}/{paths.length}</span>
          </div>
        </div>

        <div className="paths-comparison">
          <h4>Path Comparison</h4>
          {paths.map((path, idx) => {
            // Same color logic as graph
            const pathColors = [
              '#4CAF50', // Green
              '#2196F3', // Blue
              '#9C27B0', // Purple
              '#FF9800', // Orange
              '#F44336', // Red
              '#00BCD4', // Cyan
            ];
            const pathColor = pathColors[idx] || pathColors[pathColors.length - 1];
            
            return (
              <div 
                key={idx} 
                className={`path-item path-idx-${idx}`}
                style={{ borderColor: pathColor }}
              >
                <div className="path-header">
                  <span className="path-label">
                    {idx === 0 ? '⭐' : `${idx + 1}`} Path {idx + 1}
                  </span>
                  <span className="path-confidence" style={{ color: pathColor }}>
                    {(path.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="path-bar">
                  <div 
                    className="path-fill"
                    style={{ 
                      width: `${path.confidence * 100}%`,
                      background: `linear-gradient(90deg, ${pathColor} 0%, ${pathColor}dd 100%)`
                    }}
                  ></div>
                </div>
                <div className="path-details">
                  <span>{path.depth} hops</span>
                  <span>{path.coinbase_reached ? '⛏️ Coinbase' : '🔍 Partial'}</span>
                  <span>{path.known_entities?.length || 0} entities</span>
                </div>
              </div>
            );
          })}
        </div>

        {allEntities.length > 0 && (
          <div className="entities-summary">
            <h4>🏷️ All Known Entities ({allEntities.length})</h4>
            <div className="entities-grid">
              {allEntities.map((entity, idx) => (
                <div 
                  key={idx} 
                  className={`entity-badge risk-${entity.risk_level} clickable`}
                  onClick={() => setSelectedEntity(entity)}
                  title="Click for details"
                >
                  <span className="entity-emoji">
                    {entity.entity_type === 'exchange' ? '🏦' : 
                     entity.entity_type === 'mining_pool' ? '⛏️' :
                     entity.entity_type === 'mixer' ? '🌪️' :
                     entity.entity_type === 'darknet' ? '🕵️' : '❓'}
                  </span>
                  <span className="entity-label-short">{entity.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NEW: Aggregate Tainted Addresses from all paths */}
        {(() => {
          const allTainted = paths.flatMap(p => p.tainted_addresses || []);
          // Remove duplicates by address
          const uniqueTainted = Array.from(
            new Map(allTainted.map(t => [t.address, t])).values()
          );
          
          if (uniqueTainted.length > 0) {
            return (
              <div className="entities-section tainted-section">
                <h4>🚨 Tainted Addresses Found</h4>
                <div className="entities-list">
                  {uniqueTainted.map((tainted, idx) => (
                    <div 
                      key={idx} 
                      className={`entity-card tainted-card ${tainted.verified ? 'verified' : 'auto-detected'}`}
                      title={tainted.verified ? 'Verified tainted address' : 'Auto-detected from WalletExplorer'}
                    >
                      <div className="entity-header">
                        <span className="entity-name">
                          {tainted.verified ? '🚨' : '⚠️'} {tainted.description || tainted.category}
                        </span>
                        {!tainted.verified && (
                          <span className="entity-type auto-badge">Auto</span>
                        )}
                      </div>
                      <div className="entity-details">
                        <span className="entity-address" title={tainted.address}>
                          {tainted.address.substring(0, 20)}...
                        </span>
                        <span className={`taint-severity severity-${tainted.severity}`}>
                          Severity: {tainted.severity}/10
                        </span>
                      </div>
                      <div className="entity-depth">
                        Depth: {tainted.depth} • 
                        {tainted.direction === 'input' ? ' ⬅️ Input' : ' ➡️ Output'} • 
                        {tainted.value_btc?.toFixed(4) || '?'} BTC
                      </div>
                      {tainted.tags && tainted.tags.length > 0 && (
                        <div className="tainted-tags-inline">
                          {tainted.tags.slice(0, 3).map((tag, tagIdx) => (
                            <span key={tagIdx} className="tag-badge">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        })()}

        {/* Show fraud score for highest risk path */}
        {(() => {
          // Find path with highest fraud score
          const pathsWithScores = paths.filter(p => p.fraud_score);
          
          if (pathsWithScores.length > 0) {
            const highestRiskPath = pathsWithScores.reduce((max, p) => 
              (p.fraud_score.final_score > (max.fraud_score?.final_score || 0)) ? p : max
            );
            
            return (
              <>
                <div className="fraud-score-note">
                  <strong>⚠️ Showing highest risk path</strong> (Path with score: {highestRiskPath.fraud_score.final_score})
                </div>
                <FraudScoreCard 
                  fraudScore={highestRiskPath.fraud_score} 
                  taintedAddresses={highestRiskPath.tainted_addresses || []}
                />
              </>
            );
          } else {
            return null;
          }
        })()}
      </div>
    );
  };

  const renderTreeStats = (data) => {
    const paths = data.paths;
    const avgConfidence = paths.reduce((sum, p) => sum + p.confidence, 0) / paths.length;
    const maxDepth = Math.max(...paths.map(p => p.depth));
    const coinbaseReached = paths.filter(p => p.coinbase_reached).length;
    
    return (
      <div className="stats-content">
        <div className="stat-card primary">
          <div className="stat-icon">🌳</div>
          <div className="stat-info">
            <span className="stat-label">Total Paths</span>
            <span className="stat-value">{paths.length}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📏</div>
          <div className="stat-info">
            <span className="stat-label">Max Depth</span>
            <span className="stat-value">{maxDepth}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⛏️</div>
          <div className="stat-info">
            <span className="stat-label">Coinbase</span>
            <span className="stat-value">{coinbaseReached}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <span className="stat-label">Avg Confidence</span>
            <span className="stat-value">{(avgConfidence * 100).toFixed(0)}%</span>
          </div>
        </div>

        <div className="tree-distribution">
          <h4>Path Distribution</h4>
          <div className="distribution-chart">
            {['main', 'secondary', 'branch'].map(type => {
              const count = paths.filter(p => p.path_type === type).length;
              if (count === 0) return null;
              return (
                <div key={type} className="distribution-item">
                  <span className="distribution-label">{type}</span>
                  <div className="distribution-bar">
                    <div 
                      className={`distribution-fill ${type}`}
                      style={{ width: `${(count / paths.length) * 100}%` }}
                    ></div>
                  </div>
                  <span className="distribution-value">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="trace-statistics">
        <div className="stats-header">
          <h3>📊 Trace Statistics</h3>
          <span className="stats-mode">{mode}</span>
        </div>
        
        {mode === 'smart' && renderSmartStats(traceData)}
        {(mode === 'multi_path' || mode === 'multi-path') && renderMultiPathStats(traceData)}
        {mode === 'tree' && renderTreeStats(traceData)}
      </div>
      
      {selectedEntity && (
        <EntityDetailsModal 
          entity={selectedEntity} 
          onClose={() => setSelectedEntity(null)} 
        />
      )}
    </>
  );
}

export default TraceStatistics;
